import React, {useState, useEffect} from 'react';
import CollapseButton from '../../../utilities/CollapseButton/CollapseButton';
import {transactionType, actionDescription, ProductCode} from '../../Page/TransactionSummaryPage/Shared/Constants';
import {
    setPriceCyclesDescription,
    constructPurchasedPrice,
    checkSectionValid,
    checkRegularPayment,
    setStatusStyle,
} from '../../Page/TransactionSummaryPage/Shared/HelperFunctions';
import TradeInInfoEquipment from './TradeInInfoEquipment';
import FAQCollapse from '../../Shared/FAQCollapse/FAQCollapse';

function isCancelProductsSection(dealType, reasonCode) {
    return dealType === transactionType.CancelDeal ||
        (dealType === transactionType.Disconnect && reasonCode === 'DL');
}

function shouldShowEquipmentSection(equipmentSummary, cancelProductsSection) {
    if (checkSectionValid(equipmentSummary)) {
        return true;
    }

    if (!cancelProductsSection || !equipmentSummary) {
        return false;
    }

    return (equipmentSummary.purchasedEquipment?.length > 0) ||
        (equipmentSummary.loanedEquipment?.length > 0) ||
        (equipmentSummary.tradeInInfoEquipment?.length > 0);
}

const EquipmentAccessorySummary = ({data, context, equipmentSummary, dealType, currentSubscriber, reasonCode}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [allEquipments, setEquipments] = useState(<div/>);
    const cancelProductsSection = isCancelProductsSection(dealType, reasonCode);
    const [showComponent, setShowComponent] = useState(
        shouldShowEquipmentSection(equipmentSummary, cancelProductsSection),
    );

    useEffect(() => {
        if (currentSubscriber === undefined || cancelProductsSection) {
            setEquipments(setDetails(equipmentSummary, dealType, reasonCode));
            setShowComponent(shouldShowEquipmentSection(equipmentSummary, cancelProductsSection));
            return;
        }

        const equipmentBySubscriber = {
            loanedEquipment: equipmentSummary?.loanedEquipment?.filter(
                (loaned) => loaned.subscriber === currentSubscriber,
            ) ?? [],
            purchasedEquipment: equipmentSummary?.purchasedEquipment?.filter(
                (purchased) => purchased.subscriber === currentSubscriber,
            ) ?? [],
            tradeInInfoEquipment: equipmentSummary?.tradeInInfoEquipment?.filter(
                (tradeIn) => tradeIn.subscriber === currentSubscriber,
            ) ?? [],
        };

        if (isEmptyEquipment(equipmentBySubscriber)) {
            setShowComponent(false);
            return;
        }

        setEquipments(setDetails(equipmentBySubscriber, dealType, reasonCode));
        setShowComponent(true);
    }, [equipmentSummary, currentSubscriber, dealType, reasonCode, cancelProductsSection]);


    const isEmptyEquipment = (equipmentBySubscriber) => {

        if(equipmentBySubscriber === undefined)
            return true

        const {loanedEquipment,purchasedEquipment,tradeInInfoEquipment} = equipmentBySubscriber

        return (tradeInInfoEquipment?.length === 0 || tradeInInfoEquipment === undefined) && (purchasedEquipment?.length === 0 || purchasedEquipment === undefined) && (loanedEquipment?.length === 0 || loanedEquipment === undefined);


    }
    let cardTitle =
        data?.equipmentSummaryTopic?.value === '' || equipmentSummary?.tradeInInfoEquipment?.length > 0 ? 'ציוד' : data?.equipmentSummaryTopic?.value;

    if (cancelProductsSection) {
        cardTitle = 'מוצרים והשירותים שבחרת לבטל';
    }

    return showComponent ? (
        <div className='block-frame p-2'>
            <CollapseButton cardTitle={cardTitle} setIsOpen={setIsOpen} isOpen={isOpen}/>
            {isOpen && (
                <div className='block-frame__container'>
                    <div className='equipment-summary'>{allEquipments}</div>
                </div>
            )}
        </div>
    ) : null;
};

function setDetails(equipmentSummary, dealType, reasonCode) {

    if (!equipmentSummary ||
        (equipmentSummary.loanedEquipment == undefined &&
            equipmentSummary.purchasedEquipment == undefined &&
            equipmentSummary.tradeInInfoEquipment == undefined)) {
        return;
    }

    const cancelProductsSection = isCancelProductsSection(dealType, reasonCode);

    const isDealTypeChangeOrReplace = dealType === transactionType.Replace || dealType === transactionType.Change;
    const isContainPackage = [];
    const standAloneKey = 'StandAlone';
    let sortedEquipmentDict = [];
    sortedEquipmentDict[standAloneKey] = [];

    function isBlankOrEmpty(input) {
        const trimmedString = input.trim();
        return trimmedString === '';
    }

    // loop all given equipments
    for (const equipment of equipmentSummary?.purchasedEquipment ?? []) {
        const {packageLineId, orderLineId, ...rest} = equipment;

        if (isBlankOrEmpty(packageLineId) === false) isContainPackage.push(packageLineId);

        // Is 'packageLineId' empty? Add it under 'StandAloneKey'
        if (packageLineId.trim().length === 0) {
            sortedEquipmentDict[standAloneKey].push(equipment);
            continue;
        }

        // Is 'packageLineId' already exists? check is need to add the equipment in first place or last
        if (packageLineId in sortedEquipmentDict) {
            // Is the equipment the main element? add it first
            if (packageLineId.trim() === orderLineId.trim()) {
                const updateMainEquipment = {...rest, isOpen: false};
                sortedEquipmentDict[packageLineId].unshift(updateMainEquipment);
            } else {
                sortedEquipmentDict[packageLineId].push(equipment);
            }
            continue;
        }

        // The 'packageLineId' is not exist in the dictionary, add new key value pair
        if (packageLineId.trim() === orderLineId.trim()) {
            const updateMainEquipment = {...rest, isOpen: false};
            sortedEquipmentDict[packageLineId] = [updateMainEquipment];
        } else {
            sortedEquipmentDict[packageLineId] = [equipment];
        }
    }
    const loanedEquipment = equipmentSummary?.loanedEquipment?.map((equipment, idx) => {
        const isCancelled = equipment.action.toLowerCase() === 'remove' || equipment.action.toLowerCase() === 'disconnect';
        let rowStyle = 'block-frame__row' + (isDealTypeChangeOrReplace ? ' with-status-layout' : '');

        if (!checkRegularPayment(equipment.priceCycles)) {
            rowStyle += ' block-frame__row--1';
        }

        return (
            <div className='block-frame__list-info' key={idx.toString() + equipment.name}>
                <div className={rowStyle}>
                    <div className='block-frame__status-holder'>
                        {isDealTypeChangeOrReplace && (
                            <p className={'block-frame__status block-frame__status' + setStatusStyle(equipment.action)}>
                                {actionDescription[equipment.action]}
                            </p>
                        )}
                        <div>
                            <label className={isCancelled ? 'cancelled' : ''}>{equipment.name}</label>
                            {equipment.productCode === ProductCode.SBEQU && (
                                <label className={'block-frame__amount-unit'}>{equipment.amount} יחידות</label>
                            )}
                        </div>
                    </div>
                    {setPriceCyclesDescription(equipment.priceCycles, '', isCancelled)}
                </div>
            </div>
        );
    });

    let purchasedEquipment = [];
    const tradeInInfoEquipment = equipmentSummary?.tradeInInfoEquipment;

    // Handle 'StandAlone' case separately
    const standAloneElement = sortedEquipmentDict[standAloneKey]?.map((equipment, idx) => {
        return (
            <div
                className='block-frame__list-info stand-alone'
                key={idx.toString() + equipment.name}>
                <p className={'block-frame__status block-frame__status' + setStatusStyle(equipment.action)}>
                    {actionDescription[equipment.action]}
                </p>
                <div className='block-frame__row block-frame__row--2'>
                    <label>{equipment.name} {equipment.isSubSim && <span>(משני)</span>}</label>

                    {constructPurchasedPrice(equipment.price, equipment.numOfInstallments, equipment.nextPaymentAmount, dealType, equipment.effectiveInterest,equipment.cashPrice)}
                </div>
                {equipment.campaignDesc &&
                    <>
                        <p style={{'margin': '0px 4px 0 0'}}
                           className={'block-frame__status block-frame__status--4'}>
                            מבצע
                        </p>
                        <label>{equipment.campaignDesc}</label>
                    </>

                }
            </div>
        );
    });

    purchasedEquipment.push(standAloneElement);

    // Process the rest of the equipment keys
    for (const equipmentKey in sortedEquipmentDict) {
        if (equipmentKey === standAloneKey) {
            // Skip 'StandAlone' since it was handled separately
            continue;
        }

        let element;
        const hasChildren = sortedEquipmentDict[equipmentKey].length > 1;

        element = sortedEquipmentDict[equipmentKey].map((equipment, idx) => {
            //here setOpen
            let isOpenCollapse = equipment.isOpen;
            if (idx === 0) {
                return (
                    <div key={idx.toString() + equipment.name}>
                        <p style={{'margin': '0 4px 0 0'}}
                           className={'block-frame__status block-frame__status' + setStatusStyle(equipment.action)}>
                            {actionDescription[equipment.action]}
                        </p>

                        <FAQCollapse
                            isOpen={isOpenCollapse}
                            group={sortedEquipmentDict[equipmentKey].splice(1, sortedEquipmentDict[equipmentKey].length)}
                            hasChildren={hasChildren}
                            dealType={dealType}
                            cardTitle={equipment.name}
                            campaignDesc={equipment.campaignDesc}
                            price={constructPurchasedPrice(equipment.price, equipment.numOfInstallments, equipment.nextPaymentAmount, dealType, equipment.effectiveInterest,equipment.cashPrice)}
                        />
                    </div>
                );
            }
        });

        purchasedEquipment.push(element);
    }

    const hasLoanedEquipment = loanedEquipment?.length > 0 && loanedEquipment[0] !== undefined;
    const hasPurchasedEquipment = purchasedEquipment.length > 0 && purchasedEquipment[0] !== undefined;
    const hasTradeInEquipment = tradeInInfoEquipment?.length > 0 && tradeInInfoEquipment[0] !== undefined;

    return (
        <React.Fragment>
            {hasLoanedEquipment && dealType !== transactionType.Accessory && (
                <div>
                    <label className='block-frame__sub-title'>ציוד בהשכרה</label>
                    <hr/>
                    {loanedEquipment}
                </div>
            )}
            {hasPurchasedEquipment && (
                <div>
                    {dealType !== transactionType.Accessory && (
                        <label
                            className={hasLoanedEquipment && hasPurchasedEquipment ? 'block-frame__sub-title add-margin-top' : 'block-frame__sub-title'}>
                            {cancelProductsSection ? '' : 'ציוד במכירה'}
                        </label>
                    )}
                    <hr/>
                    {purchasedEquipment.map((equipment) => {
                        return equipment;
                    })}

                    {/*{mappedResult.length >0 && <div> <FAQCollapse isOpen={isOpen} setIsOpen={setOpen} cardTitle={mappedResult[0].group[0].name} /> </div>}*/}
                    {/*{true && (<span>{purchasedEquipment}</span>)}*/}
                </div>
            )}
            {hasTradeInEquipment && (
                <div className='mt-5'>
                    <label className='block-frame__sub-title'>ציוד בטרייד-אין</label>
                    <hr/>
                    <div className='table'>
                        <TradeInInfoEquipment tradeInInfoEquipment={tradeInInfoEquipment}/>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

export default EquipmentAccessorySummary;
